import axios from 'axios';
import Logger from '../functions/logging';

import settings from '../libs/config';
import { secondsToMinutes, isNumber } from '../functions/utils';
import ApiError from './apiError.js';
import {
	APPLETV_PLUS_QUESTION,
	APPLETV_PLUS_ENTRY,
	IWATVP_PLUS_NAVIGATION,
	APPLETV_PLUS_ASSET_ENTRY,
	SVOD_ASSET_NAVIGATION,
	PEACOCK_ADUNIT,
	PEACOCK_ADCHOICE_USER_SELECTION,
	SVOD_HIDDEN_QUESTION_TYPES
} from '../components/svod/constants';

const endpointPanel = settings.api_host + "/event/panel";
const endpointScene = settings.api_host + "/event/scene";
const endpointSubmitPollResponseWithQuestionTid = settings.api_host + "/poll/submit_poll_response_with_question_tid";
const endpointUserResponses = settings.api_host + "/panelresponses";

const REGISTRATION_PARAMS = ['ethnicity_hispanic', 'ethnicity_race', 'ethnicity_recode', 'gender', 'age'];

const createHiddenPanelResponse = (group, panel, psIndex, qid, aid, position, user, user_token) => {
	return {
		user_token,
		qid,
		aid,
		position,
		group_id: group._id,
		panel_segment: psIndex,
		user_id: user.user_id,
		panel_id: panel._id,
		timecode: 0,
		duration: 2,
		feedback_pro: 1,
		portal_created: true
	};
};

const returnResponsesFromAnswerArray = (group, panel, psIndex, question, user, user_token, submittedAnswerPhrase) => {
	const hiddenSVODResponses = [];
	if (question.answers) question.answers.forEach((answer, ansIndex) => {
		if (submittedAnswerPhrase === answer.phrase) {
			const panelResponse = createHiddenPanelResponse(group, panel, psIndex, question.qid, answer.aid, ansIndex, user, user_token);
			hiddenSVODResponses.push(panelResponse);
		}
	});

	return hiddenSVODResponses;
};

export const createRegistrationResponses = async (group, panel, psIndex, user, user_token) => {
	if (!group) throw new Error("Missing Group");
	if (!panel || !panel.questions) throw new Error("Missing Panel");
	if (!psIndex && psIndex != 0) throw new Error("Missing Panel Segment Index");
	if (!user) throw new Error("Missing User");
	if (!user_token) throw new Error("Missing User Token");

	{/* Possible user values:
		--------------------------
		user.ethnicity_enabled: true || false
		user.ethnicity_hispanic: true || false
		user.ethnicity_race: (7) ['AIAN', 'ASN', 'AA', 'NHPI', 'W', 'O', 'NA']
		user.ethnicity_recode: "Hispanic" || "Black/AA" || "Asian/Other" || "White"
		user.gender: 'm' || 'f' || 'z'
		user.age: integer (0-99)
	*/}

	try {
		let registrationResponses = [];
		for (let param of REGISTRATION_PARAMS) {
			switch (param) {
				case 'ethnicity_hispanic': {
					if (user.ethnicity_enabled) {
						for (let question of panel.questions) {
							if (question.global_tid === 'hispanichiddenquestion' && question.tags && question.tags.includes('hispanic')) {
	
								if (question.answers) question.answers.forEach((answer, ansIndex) => {
									if ((user.ethnicity_hispanic === 1 && answer.phrase === 'Yes')
										|| (user.ethnicity_hispanic === 0 && answer.phrase === 'No')
									) {
										let panelResponse = createHiddenPanelResponse(group, panel, psIndex, question.qid, answer.aid, ansIndex, user, user_token);
										registrationResponses.push(panelResponse);
									}
								});
								break;
							}
						}
					}
					break;
				}
				case 'ethnicity_race': {
					if (user.ethnicity_enabled) {
						for (let question of panel.questions) {
							if (question.global_tid === 'ethnicityhiddenquestion' && question.tags && question.tags.includes('ethnicity')) {
								let position = '';
								let aid = '';
								let uniqueAIDs = [];
	
								user.ethnicity_race.forEach((ethnicity) => {
									if (question.answers) question.answers.forEach((answer, ansIndex) => {
										if (!uniqueAIDs.includes(answer.aid)
											&& answer.phrase.includes(`[[${ethnicity}]]`)
										) {
											position += `${position ? '+' : ''}${ansIndex}`;
											aid += `${aid ? '+' : ''}${answer.aid}`;
											uniqueAIDs.push(answer.aid);
										}
									});
								});
	
								if (aid) {
									let panelResponse = createHiddenPanelResponse(group, panel, psIndex, question.qid, aid, position, user, user_token);
									registrationResponses.push(panelResponse);
								}
								break;
							}
						}
					}
					break;
				}
				case 'ethnicity_recode': {
					if (user.ethnicity_enabled) {
						for (let question of panel.questions) {
							if (question.global_tid === 'racerecodehiddenquestion' && question.tags && question.tags.includes('racerecode')) {
	
								if (question.answers) question.answers.forEach((answer, ansIndex) => {
									if (user.ethnicity_recode === answer.phrase) {
										let panelResponse = createHiddenPanelResponse(group, panel, psIndex, question.qid, answer.aid, ansIndex, user, user_token);
										registrationResponses.push(panelResponse);
									}
								});
								break;
							}
						}
					}
					break;
				}
				case 'gender': {
					for (let question of panel.questions) {
						if (question.global_tid === 'genderhiddenquestion' && question.tags && question.tags.includes('gender')) {

							if (question.answers) question.answers.forEach((answer, ansIndex) => {
								if ((user.gender && user.gender === 'm' && answer.phrase === 'Male') ||
									(user.gender && user.gender === 'f' && answer.phrase === 'Female') ||
									(user.gender && user.gender === 'z' && answer.phrase === 'Non-binary')
								) {
									let panelResponse = createHiddenPanelResponse(group, panel, psIndex, question.qid, answer.aid, ansIndex, user, user_token);
									registrationResponses.push(panelResponse);
								}
							});
							break;
						}
					}
					break;
				}
				case 'age': {
					for (let question of panel.questions) {
						if (question.global_tid === 'agehiddenquestion' && question.tags && question.tags.includes('age')) {
							let userAge = parseInt(user.age);
							let position = '';
							let aid = '';
							let uniqueAIDs = [];

							if (question.answers) question.answers.forEach((answer, ansIndex) => {
								let minAge = 0;
								let maxAge = 0;
								if (answer.phrase.includes('+')) {
									minAge = parseInt(answer.phrase.replace('+', ''));
									maxAge = 100;
								}
								else {
									let ageRanges = answer.phrase.split('-');
									minAge = parseInt(ageRanges[0]);
									maxAge = parseInt(ageRanges[1]);
								}

								if (userAge >= minAge && userAge <= maxAge) {
									position += `${position ? '+' : ''}${ansIndex}`;
									aid += `${aid ? '+' : ''}${answer.aid}`;
									uniqueAIDs.push(answer.aid);
								}
							});

							if (aid) {
								let panelResponse = createHiddenPanelResponse(group, panel, psIndex, question.qid, aid, position, user, user_token);
								registrationResponses.push(panelResponse);
							}
							break;
						}
					}
					break;
				}
			}
		}

		let results = [];
		if (registrationResponses.length) {
			for (let i = 0; i < registrationResponses.length; i++) {
				const response = await createPanelResponse(registrationResponses[i], user_token, true);
				results.push(response.data);
			}
		}

		return results;
	}
	catch (error) {
		throw new ApiError(error);
	}
};

export const createSVODResponses = async (group, panel, identifier, svod, psIndex, submittedAnswerPhrase, svodQuestionType, user, user_token) => {
	if (!group) throw new Error("Missing Group");
	if (!panel || !panel.questions) throw new Error("Missing Panel");
	if (!svod) throw new Error("Missing SVOD Environment");
	if (!psIndex && psIndex != 0) throw new Error("Missing Panel Segment Index");
	if (!submittedAnswerPhrase) throw new Error("Missing Hidden Answer");
	if (!user) throw new Error("Missing User");
	if (!user_token) throw new Error("Missing User Token");

	let hiddenSVODResponses = [];
	try {
		switch (svodQuestionType) {
			case IWATVP_PLUS_NAVIGATION : {
				for (let question of panel.questions) {
					if (question.global_tid === APPLETV_PLUS_QUESTION
						&& question.tags && question.tags.includes(IWATVP_PLUS_NAVIGATION)
					) {
						hiddenSVODResponses = hiddenSVODResponses.concat(returnResponsesFromAnswerArray(group, panel, psIndex, question, user, user_token, submittedAnswerPhrase));
						break;
					}
				}
				break;
			}
			case APPLETV_PLUS_ASSET_ENTRY:
			case SVOD_ASSET_NAVIGATION: {
				for (let question of panel.questions) {
					if (identifier
						&& ((question.global_tid === `${APPLETV_PLUS_ASSET_ENTRY}-${identifier}`
						&& question.tags && question.tags.includes(APPLETV_PLUS_ASSET_ENTRY)) 
						|| (question.global_tid === `${SVOD_ASSET_NAVIGATION}-${identifier}`
							&& question.tags && question.tags.includes(SVOD_ASSET_NAVIGATION)))
					) {
						const minutesInSVOD = secondsToMinutes(submittedAnswerPhrase);
						let position = '';
						let aid = '';
						let uniqueAIDs = [];

						if (question.answers) question.answers.forEach((answer, ansIndex) => {
							let minTime = 0;
							let maxTime = 0;

							if (answer.phrase.includes('+')) {
								minTime = parseInt(answer.phrase.replace('+', ''));
								maxTime = 100;
							}
							else if (answer.phrase.includes('never')) {
								// skip this submission
							}
							else {
								let timeRanges = answer.phrase.split('-');
								minTime = parseInt(timeRanges[0]);
								maxTime = parseInt(timeRanges[1]);
							}

							if (minutesInSVOD >= minTime
								&& minutesInSVOD <= maxTime
							) {
								position += `${position ? '+' : ''}${ansIndex}`;
								aid += `${aid ? '+' : ''}${answer.aid}`;
								uniqueAIDs.push(answer.aid);
							}
						});

						if (aid) {
							let panelResponse = createHiddenPanelResponse(group, panel, psIndex, question.qid, aid, position, user, user_token);
							hiddenSVODResponses.push(panelResponse);
						}
						break;
					}
				}
				break;
			}
			case PEACOCK_ADCHOICE_USER_SELECTION: {
				for (let question of panel.questions) {
					if (question.global_tid === PEACOCK_ADCHOICE_USER_SELECTION
						&& question.tags && question.tags.includes(PEACOCK_ADUNIT)
					) {
						hiddenSVODResponses = hiddenSVODResponses.concat(returnResponsesFromAnswerArray(group, panel, psIndex, question, user, user_token, submittedAnswerPhrase));
						break;
					}
				}
				break;	
			}
		}

		let results = [];
		if (hiddenSVODResponses.length) {
			for (let panelResponse of hiddenSVODResponses) {
				panelResponse.svod_id = svod._id;
				let response = await createPanelResponse(panelResponse, user_token);
				results.push(response);
			}
		}

		return results;
	}
	catch (error) {
		throw new ApiError(error);
	}
};

export const createPanelResponse = async (panelResponse, user_token, checkForResponse) => {
	if (!user_token) throw new Error("Missing User Token");
	
	let params = {
		user_token: user_token,
		group_id: panelResponse.group_id,
		panel_segment: panelResponse.panel_segment,
		user_id: panelResponse.user_id,
		timecode: panelResponse.timecode,
		panel_id: panelResponse.panel_id,
		qid: panelResponse.qid,
		aid: panelResponse.aid,
		qtype: panelResponse.qtype,
		position: panelResponse.position,
		duration: panelResponse.duration,
		language: panelResponse.language,
		feedback_pro: panelResponse.feedback_pro,
		started_at: panelResponse.started_at,
	};

	// svod
	if (checkForResponse) params.checkForResponse = checkForResponse;
	if (panelResponse.asset_id) params.asset_id = panelResponse.asset_id;
	if (panelResponse.svod_id) params.svod_id = panelResponse.svod_id;
	if (panelResponse.createdAt) params.createdAt = panelResponse.createdAt;

	// maxdiff
	if (panelResponse.tasks_order) params.tasks_order = panelResponse.tasks_order;
	if (panelResponse.tasks_responses) params.tasks_responses = panelResponse.tasks_responses;
	if (panelResponse.version_id) params.version_id = panelResponse.version_id;
	if (panelResponse.version_title) params.version_title = panelResponse.version_title;
	if (panelResponse.best_worst_results) params.best_worst_results = panelResponse.best_worst_results;

	// conversational follow-ups
	if (panelResponse.followups) params.followups = panelResponse.followups;

	Logger.log("responses.js", "createPanelResponse()", panelResponse, params);

	try {
		const response = await axios({
			method: 'post',
			url: endpointPanel,
			data: params,
			config: { headers: { 'Content-Type': 'multipart/form-data' } }
		});

		const data = response.data;
		return data.result;
	} catch (error) {
		throw new ApiError(error);
	}
};

export const createSceneResponse = async (sceneResponse, user_token) => {
	if (!user_token) throw new Error("Missing User Token");
	let params = {
		user_token: user_token,
		user_id: sceneResponse.user_id,
		ext_video_id: sceneResponse.ext_video_id,
		response: sceneResponse.response,
		emotions : sceneResponse.emotions,
		panel_segment: sceneResponse.panel_segment,
		group_id: sceneResponse.group_id,
		scenecode: sceneResponse.scenecode,
		timecode: sceneResponse.timecode,
	};
	try {
		const response = await axios({
			method: 'post',
			url: endpointScene,
			data: params,
			config: { headers: { 'Content-Type': 'multipart/form-data' } }
		});

		const data = response.data;
		return data.result;
	} catch (error) {
		throw new ApiError(error);
	}
};

export const submitPollResponseWithQuestionTid = async(urlParams, user_token, poll_ids) => {
	if (!user_token) throw new Error("Missing User Token");
	if (!poll_ids) return;
	
	let params = {
		user_token: user_token,
		question_tid: urlParams.question_tid,
		answer_index: urlParams.answer_index,
		group_id: urlParams.gid,
		panel_segment_index: urlParams.panelSegmentIndex,
		poll_ids: poll_ids.toString(),
	};
	try {
		const response = await axios({
			method: 'post',
			url: endpointSubmitPollResponseWithQuestionTid,
			data: params,
			config: { headers: { 'Content-Type': 'multipart/form-data' } }
		});

		const data = response.data;
		return data.result;
	} catch (error) {
		throw new ApiError(error);
	}
};

export const getUserResponsesInfo = async (group_id, panel_id, panel_segment_index, survey_id, user_id, user_token) => { 
	if (!group_id) throw new Error("Missing Group ID");
	if (!panel_id && !survey_id) throw new Error("Missing Panel ID And Survey ID");
	if (!panel_segment_index && !isNumber(panel_segment_index)) throw new Error("Missing Panel Segment Index");
	if (!user_id) throw new Error("Missing User ID");
	if (!user_token) throw new Error("Missing User Token Info");

	const params = { group_id, panel_id, panel_segment_index, survey_id, user_id, user_token };

	try {
		const response = await axios.get(endpointUserResponses, {
			params
		});

		const data = response.data;
		return data.result;
	}
	catch (error) {
		Logger.log(error);
		throw new ApiError(error);
	}
};


// WEBPACK FOOTER //
// ./src/api/responses.js